import { buildI18n } from "@repo/shared";
import type {
  LocalizedString,
  ProductType,
  ProductTypeAssessmentMode,
  Translated,
} from "@repo/shared/types";
import { ClipboardList, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TranslationButton } from "@/components/ui/translation-button";
import * as pcApi from "@/lib/api/portfolio-collections";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import {
  useCreateProductType,
  useDeleteProductType,
  useToggleProductTypeActive,
  useToggleProductTypeDefault,
  useUpdateProductType,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";

import { AssessmentFormDialog } from "./assessment-form-dialog";
import { T } from "./product-types-table.i18n";

/** The type's effective configuration: no modes is "Simple", both is combined. */
function assessmentLabel(
  modes: ProductTypeAssessmentMode[] | undefined,
  t: Translated<typeof T>,
): string {
  const hasQuiz = modes?.includes("QUIZ") ?? false;
  const hasQuestionnaire = modes?.includes("QUESTIONNAIRE") ?? false;
  if (hasQuiz && hasQuestionnaire) return t.modeBoth;
  if (hasQuiz) return t.modeQuiz;
  if (hasQuestionnaire) return t.modeQuestionnaire;
  return t.modeSimple;
}

type ProductTypesTableProps = {
  portfolioCollectionId: string;
  productTypes: ProductType[];
};

export function ProductTypesTable({ portfolioCollectionId, productTypes }: ProductTypesTableProps) {
  const createProductType = useCreateProductType();
  const updateProductType = useUpdateProductType();
  const deleteProductType = useDeleteProductType();
  const toggleProductTypeActive = useToggleProductTypeActive();
  const toggleProductTypeDefault = useToggleProductTypeDefault();

  const isPending =
    createProductType.isPending ||
    updateProductType.isPending ||
    deleteProductType.isPending ||
    toggleProductTypeActive.isPending ||
    toggleProductTypeDefault.isPending;

  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  // Helper to get localized value
  const getName = (i18n: LocalizedString) => localize(i18n);

  // Build i18n object covering the user's language plus every tenant-supported
  // language — storing only the creator's language leaves other locales blank.

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingType, setEditingType] = useState<ProductType | null>(null);
  const [editName, setEditName] = useState("");

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<string | null>(null);

  const [assessmentFor, setAssessmentFor] = useState<ProductType | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;

    createProductType.mutate(
      {
        collectionId: portfolioCollectionId,
        data: { nameI18n: buildI18n(newName, userLanguage, tenantSupportedLanguages) },
      },
      {
        onSuccess: () => {
          setNewName("");
          setIsAdding(false);
        },
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToAdd, "destructive");
        },
      },
    );
  };

  const handleUpdate = async () => {
    if (!editingType || !editName.trim()) return;

    // Merge existing i18n with the updated value for the current language
    const updatedI18n: LocalizedString = {
      ...editingType.nameI18n,
      [userLanguage]: editName,
    };

    updateProductType.mutate(
      {
        collectionId: portfolioCollectionId,
        ptId: editingType.id,
        data: { nameI18n: updatedI18n },
      },
      {
        onSuccess: () => {
          setEditingType(null);
          setEditName("");
        },
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToUpdate, "destructive");
        },
      },
    );
  };

  const handleTranslationSave = async (typeId: string, nameI18n: LocalizedString) => {
    updateProductType.mutate(
      {
        collectionId: portfolioCollectionId,
        ptId: typeId,
        data: { nameI18n },
      },
      {
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToUpdate, "destructive");
        },
      },
    );
  };

  // QL-168 — a type the server would refuse to delete shows why on the
  // Delete click, before the confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (ptId) => pcApi.checkDeleteProductType(portfolioCollectionId, ptId),
    onAllowed: (ptId) => {
      setTypeToDelete(ptId);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDelete, "destructive"),
  });

  const handleDelete = async () => {
    if (!typeToDelete) return;

    deleteProductType.mutate(
      {
        collectionId: portfolioCollectionId,
        ptId: typeToDelete,
      },
      {
        onSuccess: () => {
          setTypeToDelete(null);
        },
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToDelete, "destructive");
          setTypeToDelete(null);
        },
      },
    );
  };

  const handleToggleActive = async (id: string) => {
    toggleProductTypeActive.mutate(
      {
        collectionId: portfolioCollectionId,
        ptId: id,
      },
      {
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToUpdate, "destructive");
        },
      },
    );
  };

  const handleToggleDefault = (id: string) => {
    toggleProductTypeDefault.mutate(
      {
        collectionId: portfolioCollectionId,
        ptId: id,
      },
      {
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToUpdate, "destructive");
        },
      },
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          {!isAdding && (
            <Button onClick={() => setIsAdding(true)} disabled={isPending}>
              {t.addProductType}
            </Button>
          )}
        </div>

        {productTypes.length === 0 && !isAdding ? (
          <EmptyState>{t.noProductTypes}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.assessment}</TableHead>
                <TableHead>{t.default}</TableHead>
                <TableHead>{t.active}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productTypes.map((type) => (
                <TableRow key={type.id}>
                  <TableCell>
                    {editingType?.id === type.id ? (
                      <div className="flex gap-2">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder={t.productTypeName}
                          autoFocus
                          disabled={isPending}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleUpdate();
                            if (e.key === "Escape") {
                              setEditingType(null);
                              setEditName("");
                            }
                          }}
                        />
                        <Button size="sm" onClick={handleUpdate} disabled={isPending}>
                          {t.save}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingType(null);
                            setEditName("");
                          }}
                          disabled={isPending}
                        >
                          {t.cancel}
                        </Button>
                      </div>
                    ) : (
                      <span className="font-medium">{getName(type.nameI18n)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{assessmentLabel(type.assessmentModes, t)}</Badge>
                  </TableCell>
                  <TableCell>
                    <Switch
                      aria-label={`${t.default}: ${getName(type.nameI18n)}`}
                      checked={type.isDefault}
                      onCheckedChange={() => handleToggleDefault(type.id)}
                      disabled={isPending || !type.isActive}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      aria-label={`${t.active}: ${getName(type.nameI18n)}`}
                      checked={type.isActive}
                      onCheckedChange={() => handleToggleActive(type.id)}
                      disabled={isPending}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {editingType?.id !== type.id && (
                      <div className="flex items-center justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          tooltip={t.assessment}
                          onClick={() => setAssessmentFor(type)}
                          disabled={isPending}
                        >
                          <ClipboardList className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          tooltip={t.edit}
                          variant="ghost"
                          onClick={() => {
                            setEditingType(type);
                            setEditName(getName(type.nameI18n));
                          }}
                          disabled={isPending}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <TranslationButton
                          value={type.nameI18n}
                          onSave={(nameI18n) => handleTranslationSave(type.id, nameI18n)}
                          supportedLanguages={tenantSupportedLanguages}
                          disabled={isPending}
                        />
                        <Button
                          size="icon"
                          tooltip={t.delete}
                          variant="ghost"
                          loading={checkingId === type.id}
                          onClick={() => requestDelete(type.id)}
                          disabled={isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {isAdding && (
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t.placeholder}
              autoFocus
              disabled={isPending}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
                if (e.key === "Escape") {
                  setIsAdding(false);
                  setNewName("");
                }
              }}
            />
            <Button size="sm" onClick={handleAdd} disabled={isPending}>
              {t.add}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setIsAdding(false);
                setNewName("");
              }}
              disabled={isPending}
            >
              {t.cancel}
            </Button>
          </div>
        )}
      </div>

      <AssessmentFormDialog
        open={!!assessmentFor}
        collectionId={portfolioCollectionId}
        productType={assessmentFor}
        onClose={() => setAssessmentFor(null)}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteProductType}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
